// src/careComponents/components/tourBookingButton.tsx
import * as React from "react";
import {
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";

type BookingProviderType = "plany365" | "msBookings" | "external";
type Actor = "PROVIDER" | "REFERRER";

type ParamMapCtx = {
  actor: Actor;
  lead: {
    id: string | number;
    business?: { name?: string | null } | null;
    coordinatorName?: string | null;
    coordinatorEmail?: string | null;
    referrerContact?: {
      id: string | number;
      name?: string | null;
      email?: string | null;
      phone?: string | null;
    } | null;
    providerContact?: {
      name?: string | null;
      email?: string | null;
      phone?: string | null;
    } | null;
  };
  intent: "tour";
};

type ParamMap = (
  ctx: ParamMapCtx
) => Record<string, string | number | boolean | null | undefined>;

export type BusinessBookingConfig = {
  id: string;
  type: BookingProviderType;
  url: string; // short code or absolute
  createdByAgentId?: string | null;
  allowEmbed?: boolean;
};

type TourBookingButtonPropsBase = {
  lead: ParamMapCtx["lead"];
  actor: Actor;
  label?: string;
  disabled?: boolean;
  hideButton?: boolean;
  autoOpenToken?: string | number | null;
  /** Compose /embed/:bizSlug/... for relative short-codes (default: true) */
  forceEmbedPath?: boolean;
  iframeHeight?: number; // default 620
  onLaunch?: (info: {
    url?: string;
    usedProvider?: BookingProviderType;
    valid: boolean;
    allowEmbed?: boolean;
  }) => void;
};

type TourBookingButtonPropsOld = TourBookingButtonPropsBase & {
  booking: {
    type: BookingProviderType;
    url: string;
    allowEmbed?: boolean;
    paramMap?: ParamMap;
  };
};

type TourBookingButtonPropsNew = TourBookingButtonPropsBase & {
  type: BookingProviderType;
  businessBookings: BusinessBookingConfig[];
  bizSlug?: string;
  rootClientPath?: string;
  paramMap?: ParamMap;
};

export type TourBookingButtonProps =
  | TourBookingButtonPropsOld
  | TourBookingButtonPropsNew;

/* ------------------------------ Helpers -------------------------------- */

function buildUrl(base: string, params: Record<string, any> = {}) {
  try {
    const url = new URL(base, window.location.origin);
    Object.entries(params).forEach(([k, v]) => {
      if (v == null || v === "") return;
      url.searchParams.set(k, String(v));
    });
    return url.toString();
  } catch {
    return base;
  }
}

const defaultParamMap: ParamMap = ({ actor, lead }) => {
  const who =
    actor === "PROVIDER" ? lead.providerContact : lead.referrerContact;
  const currentEntry =
    typeof window !== "undefined"
      ? `${window.location.pathname}${window.location.search || ""}`
      : "";
  return {
    referralLeadId: lead.id,
    entry: currentEntry,
    name: who?.name ?? "",
    email: who?.email ?? "",
    phone: who?.phone ?? "",
    coordinatorName: lead.coordinatorName ?? "",
    coordinatorEmail: lead.coordinatorEmail ?? "",
    notes: `Tour for lead #${lead.id}${
      lead.business?.name ? ` — ${lead.business.name}` : ""
    }`,
  };
};

function isAbsolute(href?: string) {
  return !!href && (/^https?:\/\//i.test(href) || /^\/\//.test(href));
}

// Only fields that affect URL resolution — keeps memo stable
function projectBookingsForMemo(list?: BusinessBookingConfig[]) {
  return (list ?? []).map((b) => ({
    createdByAgentId: b.createdByAgentId ?? null,
    url: b.url ?? "",
    allowEmbed: !!b.allowEmbed,
    type: b.type,
  }));
}

/** Compose a headerless embed path for short codes. Returns null if slug missing. */
function composeEmbedPath(
  shortCode: string,
  bizSlug?: string | null,
  rootClientPath?: string | null
): string | null {
  const slug = String(bizSlug ?? "").replace(/^\/+|\/+$/g, "");
  if (!slug) return null; // 🛑 do NOT create /booking/... without a business slug
  const base = String(rootClientPath ?? "").replace(/\/+$/, "");
  const tail = String(shortCode).replace(/^\/+/, "");
  return base
    ? `${base}/embed/${slug}/booking/${tail}`
    : `/embed/${slug}/booking/${tail}`;
}

/* ------------------------------ Component ------------------------------ */

export default function TourBookingButton(props: TourBookingButtonProps) {
  const {
    lead,
    actor,
    label = "Book Tour",
    disabled = false,
    hideButton = false,
    autoOpenToken = null,
    forceEmbedPath = true,
    iframeHeight = 820,
    onLaunch,
  } = props;

  const [open, setOpen] = React.useState(false);
  const [valid, setValid] = React.useState<boolean>(false);
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));

  const paramMap: ParamMap = React.useMemo(
    () =>
      ("booking" in props && props.booking?.paramMap) ||
      ("paramMap" in props && props.paramMap) ||
      defaultParamMap,
    [props]
  );

  const params = React.useMemo(
    () => paramMap({ actor, lead, intent: "tour" }),
    [paramMap, actor, lead]
  );

  // Pre-extract embed fields safely (union-narrowing)
  const embedBizSlug =
    "bizSlug" in props
      ? (props as TourBookingButtonPropsNew).bizSlug
      : undefined;
  const embedRootPath =
    "rootClientPath" in props
      ? (props as TourBookingButtonPropsNew).rootClientPath
      : undefined;

  const resolved = React.useMemo(() => {
    if ("booking" in props && props.booking) {
      // Old shape
      let url = props.booking.url || "";

      if (!isAbsolute(url) && forceEmbedPath) {
        // Compose only if we have a biz slug; otherwise leave invalid (show message)
        const composed = composeEmbedPath(url, embedBizSlug, embedRootPath);
        url = composed ?? "";
        if (!composed && process.env.NODE_ENV !== "production") {
          // Helpful dev breadcrumb
          // eslint-disable-next-line no-console
          console.warn(
            "[TourBookingButton] Missing bizSlug: cannot compose embed URL for short code:",
            url
          );
        }
      }

      return {
        usedProvider: props.booking.type,
        url,
        allowEmbed: props.booking.allowEmbed ?? true,
        foundAgentConfig: true,
      };
    } else {
      // New shape: prefer entries with a matching provider type and valid url.
      const {
        type,
        businessBookings = [],
        bizSlug,
        rootClientPath,
      } = props as TourBookingButtonPropsNew;

      const cfg =
        businessBookings.find(
          (b) =>
            b.type === type &&
            !!b.url
        ) ||
        businessBookings.find((b) => !!b.url) ||
        null;

      let url = cfg?.url || "";

      if (cfg?.url && !isAbsolute(cfg.url)) {
        if (forceEmbedPath) {
          const composed = composeEmbedPath(cfg.url, bizSlug, rootClientPath);
          url = composed ?? "";
        } else {
          // Only build the non-embed route if we have a slug
          const slug = String(bizSlug ?? "").replace(/^\/+|\/+$/g, "");
          if (slug) {
            const base = String(rootClientPath || "").replace(/\/+$/, "");
            const tail = String(cfg.url).replace(/^\/+/, "");
            url = base
              ? `${base}/${slug}/booking/${tail}`
              : `/${slug}/booking/${tail}`;
          } else {
            url = ""; // keep invalid rather than producing /booking/...
          }
        }
      }

      return {
        usedProvider: type,
        url,
        allowEmbed: cfg?.allowEmbed ?? true,
        foundAgentConfig: !!cfg,
      };
    }
  }, [embedBizSlug, embedRootPath, forceEmbedPath, props]);

  const fullHref = React.useMemo(() => {
    if (!resolved.url) return undefined;
    return buildUrl(resolved.url, params);
  }, [resolved.url, JSON.stringify(params)]);

  const handleOpen = () => {
    const ok = Boolean(fullHref);
    setValid(ok);
    setOpen(true);
    onLaunch?.({
      url: fullHref,
      usedProvider: resolved.usedProvider,
      valid: ok,
      allowEmbed: resolved.allowEmbed,
    });
  };

  const lastAutoOpenTokenRef = React.useRef<string>("");

  React.useEffect(() => {
    const nextToken = String(autoOpenToken || "").trim();
    if (!nextToken || nextToken === lastAutoOpenTokenRef.current || disabled) return;
    lastAutoOpenTokenRef.current = nextToken;
    handleOpen();
  }, [autoOpenToken, disabled, fullHref]);

  React.useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type !== "plany-booking-complete") return;
      const bookingUrl = String(event.data?.bookingUrl || "").trim();
      setOpen(false);
      if (!bookingUrl) return;
      navigate(bookingUrl, { replace: true });
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [navigate]);

  return (
    <>
      {!hideButton ? (
        <Button
          variant="outlined"
          size="small"
          onClick={handleOpen}
          disabled={disabled}
        >
          {label}
        </Button>
      ) : null}

      {/* IFRAME-ONLY DIALOG (no header) */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="md"
        fullScreen={isSmallScreen}
        keepMounted
        PaperProps={{
          sx: {
            overflow: "hidden",
            borderRadius: isSmallScreen ? 0 : 2,
            width: isSmallScreen ? "100%" : undefined,
            maxWidth: isSmallScreen ? "100%" : undefined,
            height: isSmallScreen ? "100%" : "min(92vh, 980px)",
            maxHeight: isSmallScreen ? "100%" : "92vh",
            m: isSmallScreen ? 0 : 2,
          },
        }}
      >
        <DialogContent
          sx={{
            p: 0,
            position: "relative",
            height: isSmallScreen ? "100vh" : `min(${iframeHeight}px, calc(92vh - 16px))`,
            minHeight: isSmallScreen ? "100vh" : "70vh",
          }}
        >
          {/* Close button overlay */}
          <Tooltip title="Close">
            <IconButton
              size="small"
              onClick={() => setOpen(false)}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
                zIndex: 2,
                bgcolor: "rgba(0,0,0,0.4)",
                color: "white",
                "&:hover": { bgcolor: "rgba(0,0,0,0.6)" },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {valid && fullHref ? (
            <Stack sx={{ height: "100%" }}>
              <iframe
                title="Booking"
                src={fullHref}
                style={{ border: 0, width: "100%", height: "100%" }}
                allow="clipboard-write *; geolocation *; microphone *; camera *; payment *"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </Stack>
          ) : (
            <Stack sx={{ p: 2, height: "100%" }} justifyContent="center">
              <Typography variant="subtitle2" gutterBottom>
                {resolved.foundAgentConfig
                  ? "Invalid or missing booking link."
                  : "No tour calendar found for this business."}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {!embedBizSlug && forceEmbedPath
                  ? "Missing business slug: cannot compose headerless embed URL for short code."
                  : resolved.foundAgentConfig
                  ? "Ask the provider or admin to update the tour booking link, or choose another provider."
                  : `We couldn’t find a valid ${
                      resolved.usedProvider ?? "booking"
                    } agent config for this business.`}
              </Typography>
            </Stack>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
